export function rerootBulkDataUri(bulkDataUri: string, pacsRoot: string): string | undefined {
  if (typeof bulkDataUri !== 'string' || !/^https?:\/\//i.test(bulkDataUri)) {
    return undefined;
  }

  try {
    const { pathname, search } = new URL(bulkDataUri);
    return `${pacsRoot.replace(/\/+$/, '')}${pathname}${search}`;
  } catch {
    return undefined;
  }
}

interface BulkDataReference {
  BulkDataURI?: string;
  Value?: unknown;
  retrieveBulkData?: () => Promise<unknown>;
}

function toBuffer(value: unknown): ArrayBuffer | null {
  if (value instanceof ArrayBuffer) {
    return value;
  }

  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer;
  }

  if (Array.isArray(value)) {
    for (const part of value) {
      const buffer = toBuffer(part);

      if (buffer?.byteLength) {
        return buffer;
      }
    }
  }

  return null;
}

function decodeBulkDataText(value: unknown): string | null {
  const buffer = toBuffer(value);

  if (!buffer?.byteLength) {
    return null;
  }

  return new TextDecoder().decode(buffer).replace(/\0+$/, '').trim() || null;
}

export async function readBulkDataText(value: unknown): Promise<string | null> {
  if (typeof value === 'string') {
    return value.replace(/\0+$/, '').trim() || null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const reference = value as BulkDataReference;

  if (reference.Value !== undefined) {
    return decodeBulkDataText(reference.Value);
  }

  if (typeof reference.retrieveBulkData !== 'function') {
    return null;
  }

  return decodeBulkDataText(await reference.retrieveBulkData());
}
